import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import moment from 'moment'

import { getDaysBetweenDates } from '~/helpers/getDaysBetweenDates'

import Tooltip from '../Tooltip'
import { Checkbox, Label, Input } from '../unform'

import * as S from './styles'

const DateInterval = ({
  initialData,
  onChange,
  showTooltip = false,
  inputNames,
  variant = 'default',
  disabledStartDate = false
}: DateIntervalProps) => {
  const today = moment().format('YYYY-MM-DD')

  const { t } = useTranslation()
  const [inputStartDate, setInputStartDate] = useState<string>(
    initialData?.start_at
  )
  const [inputEndDate, setInputEndDate] = useState<string>(initialData?.end_at)

  const colorClass = variant === 'primary' ? 'text-primary' : ''

  const inputNameStartDate = inputNames?.startDate || 'start_at'
  const inputNameEndDate = inputNames?.endDate || 'end_at'
  const inputNameCheckBox = inputNames?.checkBox || 'is_always_on'

  // Refactor
  const [alwaysOn, setAlwaysOn] = useState(!initialData?.end_at)

  const handleSetPeriod = useCallback(
    (startDate: string, endDate: string | undefined | null) => {
      if (typeof onChange === 'function') {
        const period = getDaysBetweenDates(startDate, endDate)
        onChange({ interval: period, startDate, endDate })
      }
    },
    [onChange]
  )

  const handleAlwaysOn = useCallback(() => {
    setAlwaysOn(state => !state)

    const effectiveStartDate = inputStartDate || today
    handleSetPeriod(effectiveStartDate, null)
  }, [handleSetPeriod, inputStartDate, today])

  const handleChangeDate = useCallback(
    (e: InputOnChangeData, type: 'start' | 'end') => {
      const newDate = e?.value
      if (type === 'start') {
        setInputStartDate(newDate)
        handleSetPeriod(newDate, inputEndDate)
      }

      if (type === 'end') {
        setInputEndDate(newDate)
        handleSetPeriod(inputStartDate, newDate)
      }
    },
    [handleSetPeriod, inputEndDate, inputStartDate]
  )

  /**
   * Consts
   */
  const minEndDate = useMemo(
    () => (inputStartDate ? inputStartDate : today),
    [inputStartDate, today]
  )

  return (
    <S.DateWrapper>
      <S.FormGroupDate className="start">
        <Label
          htmlFor={inputNameStartDate}
          isRequired={!disabledStartDate}
          className={colorClass}
        >
          {t('c:components.NameAndTimeInterval.startDate')}
        </Label>
        <Input
          name={inputNameStartDate}
          type="date"
          min={today}
          max={inputEndDate}
          disabled={disabledStartDate}
          onChange={e => handleChangeDate(e, 'start')}
          required
        />
      </S.FormGroupDate>

      {!alwaysOn && (
        <S.FormGroupDate>
          <Label htmlFor={inputNameEndDate} className={colorClass}>
            {t('c:components.NameAndTimeInterval.endDate')}
          </Label>
          <Input
            name={inputNameEndDate}
            type="date"
            min={minEndDate}
            onChange={e => handleChangeDate(e, 'end')}
          />
        </S.FormGroupDate>
      )}

      <S.TooltipWrapper className="checkbox">
        <div>
          <Checkbox
            name={inputNameCheckBox}
            label={t('c:components.NameAndTimeInterval.noEndDate')}
            onClick={handleAlwaysOn}
            isCheckedInitialValue={!initialData?.end_at}
            className="checkbox"
          />
        </div>
        {!!showTooltip && (
          <Tooltip
            text={t('rm:objectiveBased.setup.campaign.checkbox.tooltip')}
          />
        )}
      </S.TooltipWrapper>
    </S.DateWrapper>
  )
}

export default DateInterval
